/**
 * Client auto-update: detect a new deployed bundle and reload the page at the
 * first safe moment, so fixes reach every open tab WITHOUT the user having to
 * reload manually. The dead-socket bug of 2026-07-07 stayed alive in
 * long-open tabs precisely because a shipped fix couldn't reach them.
 *
 * Detection compares the hashed entry-bundle path (`assets/index-<hash>.js`)
 * of the currently running page against the one referenced by a freshly
 * fetched index.html. Vite regenerates the hash on every build, so a
 * different path means a newer deploy is live.
 */

export const APP_UPDATE_POLL_MS = 5 * 60 * 1000;
/** Per-tab cooldown so a misbehaving edge cache alternating between two
 * index.html versions can never cause a reload loop. */
export const APP_UPDATE_RELOAD_COOLDOWN_MS = 10 * 60 * 1000;

const RELOAD_STAMP_KEY = 'app-update:last-reload';

/** Extract the hashed entry-bundle path from an index.html document (or a
 * script src). Null when absent — e.g. the Vite dev server, where the entry
 * is /src/main.tsx and auto-update must stay off. */
export function extractBundleSignature(html: string): string | null {
  const match = html.match(/assets\/index-[A-Za-z0-9_-]+\.js/);
  return match ? match[0] : null;
}

/** Signature of the bundle the running page was loaded from. */
export function currentBundleSignature(doc: Document = document): string | null {
  const scripts = doc.querySelectorAll<HTMLScriptElement>('script[src]');
  for (const script of scripts) {
    const sig = extractBundleSignature(script.getAttribute('src') ?? '');
    if (sig) return sig;
  }
  return null;
}

/** Fetch the currently deployed index.html and extract its bundle signature.
 * Best-effort: null on any failure (offline, 5xx, unexpected content). */
export async function fetchDeployedBundleSignature(): Promise<string | null> {
  try {
    const res = await fetch('/index.html', { cache: 'no-store' });
    if (!res.ok) return null;
    return extractBundleSignature(await res.text());
  } catch {
    return null;
  }
}

/** True when an auto-reload happened within the cooldown window (per tab). */
export function reloadedRecently(now: number = Date.now()): boolean {
  try {
    const stamp = Number(sessionStorage.getItem(RELOAD_STAMP_KEY));
    return Number.isFinite(stamp) && stamp > 0 && now - stamp < APP_UPDATE_RELOAD_COOLDOWN_MS;
  } catch {
    return false;
  }
}

export function markReloaded(now: number = Date.now()): void {
  try {
    sessionStorage.setItem(RELOAD_STAMP_KEY, String(now));
  } catch {
    // sessionStorage unavailable: lose loop protection, not the update.
  }
}

/**
 * Whether it is safe to apply a pending update RIGHT NOW.
 *
 * @param callState  current call state — never yank the page during a call
 *                   (or while one is ringing/connecting)
 * @param navigating true when invoked from a route change: the user is
 *                   already getting a fresh view, a full load is invisible
 * @param hidden     document.hidden — a background tab won't notice a reload
 */
export function canApplyUpdate(opts: {
  callState: string;
  navigating: boolean;
  hidden: boolean;
  now?: number;
}): boolean {
  if (opts.callState !== 'idle') return false;
  if (reloadedRecently(opts.now)) return false;
  return opts.navigating || opts.hidden;
}
